import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, GlobeEuropeAfricaIcon, ShieldCheckIcon, SunIcon, HandThumbUpIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const Landscaping = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const expertiseItems = [
        "Landscape Planning & Design",
        "Corporate & Institutional Green Projects",
        "Farm & Agri Landscape Development",
        "Plantation & Afforestation Programs",
        "Lawn & Turf Establishment",
        "Drip Irrigation & Smart Water Systems"
    ];

    const benefits = [
        { title: "Enhances environmental balance", icon: GlobeEuropeAfricaIcon },
        { title: "Improves land aesthetics & property value", icon: SparklesIcon },
        { title: "Supports biodiversity", icon: HandThumbUpIcon },
        { title: "Promotes climate resilience", icon: SunIcon },
        { title: "Ensures structured long-term maintenance", icon: ShieldCheckIcon }
    ];

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-gray-900 text-white overflow-hidden">
                <motion.div
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
                    className="absolute inset-0 w-full h-full"
                >
                    <div className="absolute inset-0 bg-black/60 z-10"></div>
                    <img
                        src={require('../../../assets/newimages/mas website landscaping (2).webp')}
                        alt="Landscaping & Green Development"
                        className="w-full h-full object-cover opacity-80"
                    />
                </motion.div>

                <div className="relative z-20 text-center px-4 max-w-5xl mx-auto">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
                    >
                        Landscaping & <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500">Green Development</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-xl md:text-2xl text-gray-200 font-light"
                    >
                        Designing Sustainable Green Spaces with Purpose.
                    </motion.p>
                </div>
            </section>

            <section className="py-20 px-4 md:px-8 max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row gap-16 items-start">

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                        className="lg:w-1/2"
                    >
                        <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                            At <strong className="text-gray-900 font-bold">Marvelous Agri Solution</strong>, Landscaping & Green Development is more than beautification — it is strategic environmental design.
                        </p>
                        <p className="text-gray-600 text-lg leading-relaxed mb-8 font-light">
                            We create aesthetically appealing, ecologically balanced, and sustainably managed green spaces that enhance land value, environmental health, and long-term functionality.
                        </p>

                        <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-2xl mb-12">
                            <p className="text-xl font-medium text-emerald-900 italic">
                                Where nature meets structured planning.
                            </p>
                        </div>

                        <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                            Our Green Development Expertise
                        </h3>

                        <div className="space-y-4 mb-8">
                            {expertiseItems.map((item, idx) => (
                                <div key={idx} className="flex items-start gap-4 p-4 rounded-xl bg-white shadow-sm border border-gray-100 hover:border-emerald-300 hover:shadow-md transition-all">
                                    <CheckCircleIcon className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                                    <span className="text-gray-800 font-medium text-lg">{item}</span>
                                </div>
                            ))}
                        </div>

                        <p className="text-emerald-800 font-medium italic mb-10 border-b border-gray-200 pb-6">
                            Every project is designed with environmental sensitivity and long-term sustainability in mind.
                        </p>

                    </motion.div>

                    <div className="lg:w-1/2 relative space-y-12">
                        <div className="grid grid-cols-2 gap-4 mb-8">
                            <img src={require('../../../assets/newimages/mas website land scpaing.webp')} alt="Landscaping 1" className="rounded-2xl w-full h-48 object-cover shadow-lg" />
                            <img src={require('../../../assets/newimages/mas website landscaping.webp')} alt="Landscaping 2" className="rounded-2xl w-full h-48 object-cover shadow-lg translate-y-6" />
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="bg-gray-900 p-10 rounded-3xl shadow-2xl text-white relative overflow-hidden"
                        >
                            <div className="absolute -right-20 -top-20 w-80 h-80 bg-emerald-500/20 rounded-full blur-3xl text-white"></div>

                            <h3 className="text-3xl font-bold mb-8 flex items-center gap-3 relative z-10 text-emerald-400">
                                Smart & Sustainable Approach
                            </h3>

                            <h4 className="text-xl text-emerald-300 font-bold mb-6">We integrate:</h4>

                            <div className="relative z-10 space-y-4 mb-10 border-b border-gray-800 pb-8">
                                <p className="text-lg font-light text-gray-300 flex items-center gap-3">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                                    Soil assessment & plant suitability planning
                                </p>
                                <p className="text-lg font-light text-gray-300 flex items-center gap-3">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                                    Water-efficient irrigation systems
                                </p>
                                <p className="text-lg font-light text-gray-300 flex items-center gap-3">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                                    Native & climate-resilient plant selection
                                </p>
                                <p className="text-lg font-light text-gray-300 flex items-center gap-3">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                                    Eco-friendly maintenance strategies
                                </p>
                            </div>

                            <p className="text-xl text-white font-medium italic">
                                The result is a green ecosystem that thrives naturally with minimal resource wastage.
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100"
                        >
                            <h3 className="text-2xl font-bold text-gray-900 mb-8">
                                Why Choose Our Green Development Solutions?
                            </h3>

                            <div className="space-y-6">
                                {benefits.map((benefit, idx) => (
                                    <div key={idx} className="flex items-center gap-4">
                                        <div className="bg-emerald-100 p-3 rounded-xl flex-shrink-0">
                                            <benefit.icon className="w-6 h-6 text-emerald-600" />
                                        </div>
                                        <p className="text-gray-800 font-medium">{benefit.title}</p>
                                    </div>
                                ))}
                            </div>
                        </motion.div>

                    </div>
                </div>
            </section>
        </div>
    );
};

export default Landscaping;
